import { useContext } from "react";
import {
  KursusFiltersContext,
  KursusFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  KursusFilterKeys,
  KursusKategoriSetting,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import { KursusholdFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { fmuKursusKategorier } from "../../../../util/fmuKursusKategori";
import SuperDropdown from "../../../dropdown/SuperDropdown";
import DefaultDropdownItem from "../../../dropdown/DefaultDropdownItem";

// helper
const filterKey: QueryFilter<KursusFilterKeys>["filterKey"] = "kategori";

export default function KursusKategoriFilter() {
  // query filter context
  const filters = useContext(KursusFiltersContext);
  const updateFilters = useContext(KursusFiltersEventContext);

  // find the filter settings
  const settings: KursusKategoriSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // handlers
  function updateFilterHandler(selectedItem: { nr: number; navn: string }) {
    // get active list if any otherwise initialise an empty array
    const activeFilters = settings?.activeFilters || [];

    // add selected item if it does not exist in active list
    if (
      selectedItem &&
      !activeFilters.some((s) => s.kode === selectedItem.nr)
    ) {
      activeFilters.push({
        kode: selectedItem.nr,
        type: selectedItem.navn,
      });
    }

    const filters: KursusholdFilterInput[] = activeFilters.map((set) => {
      return {
        and: [
          {
            fmukursusKategoriNr: {
              eq: set.kode,
            },
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<KursusholdFilterInput>()
      .addFilter("or", filters)
      .build();

    const updatedSettings: KursusKategoriSetting = {
      activeFilters: activeFilters,
    };

    const queryFilter: QueryFilter<KursusFilterKeys, KursusKategoriSetting> = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(
    kode: KursusKategoriSetting["activeFilters"][number]["kode"],
  ) {
    const updatedFilters =
      settings?.activeFilters?.filter((set) => {
        return kode !== set.kode;
      }) || [];

    const filters: KursusholdFilterInput[] = updatedFilters.map((set) => {
      return {
        and: [
          {
            fmukursusKategoriNr: {
              eq: set.kode,
            },
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<KursusholdFilterInput>()
      .addFilter("or", filters)
      .build();

    const updatedSettings: KursusKategoriSetting = {
      activeFilters: updatedFilters,
    };

    const update = updatedSettings?.activeFilters?.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Kursuskategori"
      value="kursus-kategori"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings.activeFilters.map((filter) => (
            <AppliedFilterItem
              key={filter.kode}
              label={`${filter.type} (${filter.kode})`}
              removeFilterHandler={() => removeFilterHandler(filter.kode)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={fmuKursusKategorier}
        itemsIdKey="navn"
        itemToString={(item) => item?.navn ?? ""}
        itemToElement={(item) => <DefaultDropdownItem label={item.navn} />}
        clearInputAfterSelection={true}
        placeholder="Søg efter kursuskategori ..."
        filterKeys={["navn"]}
        onSelectedItemEvent={(event) => {
          if (event.selectedItem) {
            updateFilterHandler(event.selectedItem);
          }
        }}
      />
    </AccordionFilterItem>
  );
}
